import { useTranslation } from "react-i18next";
import type { AttributeDefView, AttributeValue } from "../api/catalog";
import { formatAttributeValue } from "./AttributeValuesView";
import Ic from "./icons.jsx";

/** Whether a stored attribute value counts as filled — what `formatAttributeValue` would not show as "—". */
export function isFilledValue(v: unknown): boolean {
  return v !== null && v !== undefined && v !== "";
}

/** The definitions that carry a value in `values`, in the type's order. */
export function filledDefs(defs: readonly AttributeDefView[], values: Readonly<Record<string, unknown>>): AttributeDefView[] {
  return defs.filter((d) => isFilledValue(values[d.key]));
}

/**
 * Filled attribute values as compact chips — the label, then the value with its unit — in the order
 * of the type (2026-10-20). Read-only by default. `isKept` + `onToggle` make every chip a tick
 * ("Sačuvaj kao šablon": the value goes into the template or not); the tick swallows its click and
 * keys so the tree row around it does not react. `onRemove` puts a × on every chip (template editor).
 */
export function AttributeValueChips({
  defs, values, isKept, onToggle, disabledReason, onRemove,
}: {
  defs: readonly AttributeDefView[];
  values: Readonly<Record<string, unknown>>;
  isKept?: (key: string) => boolean;
  onToggle?: (key: string) => void;
  /** Tick mode: the ticks are inert and this says why (title; the row shows it too). */
  disabledReason?: string | null;
  onRemove?: (key: string) => void;
}) {
  const { t } = useTranslation();
  const filled = filledDefs(defs, values);
  if (filled.length === 0) return null;
  return (
    <span className="attr-chips">
      {filled.map((d) => {
        const text = formatAttributeValue(d, values[d.key] as AttributeValue, t);
        if (isKept && onToggle) {
          const kept = isKept(d.key);
          const title = disabledReason
            ?? (kept
              ? t("templates.values.keptHint", "Vrednost ulazi u šablon — klik je izostavlja")
              : t("templates.values.leftHint", "Vrednost ne ulazi u šablon — klik je vraća"));
          return (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- keeps the tree row (TreeView onSelect) from reacting to a tick
            <label
              key={d.key}
              className={`attr-chip is-toggle${kept ? "" : " is-off"}${disabledReason ? " is-disabled" : ""}`}
              title={title}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <input type="checkbox" checked={kept} disabled={!!disabledReason} onChange={() => onToggle(d.key)} />
              <span className="attr-chip-label">{d.label}</span>{" "}
              <span className="attr-chip-value">{text}</span>
            </label>
          );
        }
        return (
          <span key={d.key} className={`attr-chip${onRemove ? " has-remove" : ""}`}>
            <span className="attr-chip-label">{d.label}</span>{" "}
            <span className="attr-chip-value">{text}</span>
            {onRemove && (
              <button
                type="button"
                className="attr-chip-x"
                aria-label={t("templates.values.remove", "Ukloni vrednost „{{label}}“", { label: d.label })}
                title={t("templates.values.remove", "Ukloni vrednost „{{label}}“", { label: d.label })}
                onClick={() => onRemove(d.key)}
              >
                <Ic.x />
              </button>
            )}
          </span>
        );
      })}
    </span>
  );
}
